import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import AlertAndLoading from 'src/components/AlertAndLoading';
import {
  getReceiptForManualUpdate,
  getReceiptManualUpdateHistory,
  getReceiptManualUpdateStatuses,
  receiptManualUpdate
} from 'src/store/slices/dynamicCatalogSlice';
import { getUser } from 'src/utils/Common';

const ORANGE = '#FF9800';
const DARK = '#424242';
const CUSTOMER = 1;
const RADIUS = '16px';

// SolutionCenterProcess ile aynı buton dili: koyu zemin, turuncu yazı
const darkButtonSx = {
  color: ORANGE,
  backgroundColor: DARK,
  borderRadius: '10px',
  px: 2.5,
  fontWeight: 600,
  '&:hover': { backgroundColor: '#212121' },
  '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
};

const lightButtonSx = {
  color: DARK,
  borderRadius: '10px',
  px: 2,
  fontWeight: 600,
  '&:hover': { backgroundColor: '#f5f5f5' }
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' }
};

const useStyles = makeStyles(() => ({
  card: {
    padding: '20px 24px',
    borderRadius: RADIUS,
    border: '1px solid #eceff1',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '700',
    fontSize: '15px',
    color: '#263238'
  },
  caption: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#78909c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  summary: {
    padding: '20px 24px',
    borderRadius: RADIUS,
    color: '#263238',
    background: 'linear-gradient(135deg, #fffaf2 0%, #fafbfc 100%)',
    border: '1px solid #f3e5d0',
    borderLeft: `5px solid ${ORANGE}`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
  },
  summaryCaption: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#90a4ae',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  summaryValue: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#263238'
  },
  fieldCard: {
    height: '100%',
    padding: '14px 16px',
    borderRadius: '14px',
    border: '1px solid #eceff1',
    backgroundColor: '#fafafa',
    transition: 'all 0.2s'
  },
  fieldCardChanged: {
    borderColor: ORANGE,
    backgroundColor: '#fff8e1',
    boxShadow: `0 0 0 3px rgba(255,152,0,0.15)`
  },
  currentValue: {
    flex: 1,
    minWidth: 0,
    fontSize: '14px',
    fontWeight: 600,
    color: '#546e7a',
    padding: '8.5px 14px',
    backgroundColor: '#eceff1',
    borderRadius: '10px',
    wordBreak: 'break-word'
  },
  arrowCircle: {
    width: '30px',
    height: '30px',
    flexShrink: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eceff1',
    color: '#b0bec5',
    transition: 'all 0.2s'
  },
  arrowCircleChanged: {
    backgroundColor: ORANGE,
    color: '#fff'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
    color: '#90a4ae'
  },
  previewRow: {
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid #ffe0b2',
    backgroundColor: '#fff8e1',
    marginBottom: '10px'
  }
}));

// Log'da kolon adıyla tutulan alanların kullanıcıya gösterilen başlıkları
const FIELD_DISPLAY_NAMES = { 'Sas Tekrar Kontrol': "Mevcut SAS'ı Koru" };
const displayFieldName = (name) => FIELD_DISPLAY_NAMES[name] || name;

const toText = (value) => (value === null || value === undefined ? '' : String(value).trim());
const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  try {
    const parsed = typeof payload === 'string' && payload ? JSON.parse(payload) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};
const initials = (name) => toText(name).split(' ').filter(Boolean).slice(0, 2).map((n) => n[0]).join('').toUpperCase() || '?';

function ChangeChips({ oldValue, newValue }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
      <Chip size="small" label={oldValue || '(boş)'} sx={{ borderRadius: '8px', textDecoration: 'line-through', color: '#78909c' }} />
      <ArrowForwardIcon sx={{ fontSize: 16, color: ORANGE }} />
      <Chip size="small" label={newValue || '(boş)'} sx={{ borderRadius: '8px', backgroundColor: ORANGE, color: '#fff', fontWeight: 600 }} />
    </Box>
  );
}

function ReceiptManualUpdateDynamic() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const SessionUser = getUser();

  const [loading, setLoading] = React.useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertType, setAlertType] = React.useState({ ErrorType: 'error', ErrorTitle: 'Uyarı' });

  const [receiptNumber, setReceiptNumber] = React.useState('');
  const [statuses, setStatuses] = React.useState([]);
  const [receipt, setReceipt] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [form, setForm] = React.useState({});
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);

  const loadStatuses = () => dispatch(getReceiptManualUpdateStatuses()).then((response) => {
    setStatuses(toArray(response.payload));
  });

  useEffect(() => {
    loadStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const showAlert = (message, isSuccess) => {
    setAlertType(isSuccess
      ? { ErrorType: 'success', ErrorTitle: 'Başarılı' }
      : { ErrorType: 'error', ErrorTitle: 'Uyarı' });
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const fillForm = (data) => {
    setForm({
      Status: data.Status ?? null,
      GpsNo: toText(data.GpsNo),
      UdInvoiceOrderNo: toText(data.UdInvoiceOrderNo),
      SasNo: toText(data.SasNo),
      SdNo: toText(data.SdNo),
      SasRecheck: toText(data.SasRecheck) || null,
      Description: ''
    });
  };

  const loadReceipt = (number) => {
    setLoading(true);
    return Promise.all([
      dispatch(getReceiptForManualUpdate(number)),
      dispatch(getReceiptManualUpdateHistory(number)),
      statuses.length === 0 ? loadStatuses() : Promise.resolve()
    ]).then(([detailResponse, historyResponse]) => {
      const data = detailResponse.payload;
      if (data && data.ReceiptNumber) {
        setReceipt(data);
        fillForm(data);
      } else {
        setReceipt(null);
        showAlert('Girilen fiş numarasına ait ürün değişim kaydı bulunamadı.', false);
      }
      setHistory(toArray(historyResponse.payload));
      setLoading(false);
    });
  };

  const searchClick = () => {
    if (!receiptNumber) {
      showAlert('Lütfen fiş numarası giriniz.', false);
      return;
    }
    loadReceipt(receiptNumber);
  };

  const clearClick = () => {
    setReceipt(null);
    setHistory([]);
    setReceiptNumber('');
  };

  const isCustomer = receipt?.CustomerType === CUSTOMER;
  const statusLabel = (id) => {
    const status = statuses.find((s) => s.Id === id);
    return id === null || id === undefined ? '' : `${id} - ${status?.Dsc || receipt?.StatusDesc || ''}`;
  };

  const textFields = isCustomer
    ? [
      { key: 'GpsNo', label: 'Gps No', maxLength: 10 },
      { key: 'UdInvoiceOrderNo', label: 'Ud Fatura No', maxLength: 100 }
    ]
    : [
      { key: 'SasNo', label: 'Sas No', maxLength: 50 },
      { key: 'SdNo', label: 'Sd No', maxLength: 50 }
    ];

  // SAS_TEKRAR_KONTROL: 1 / 0 (NULL mevcut kayıtlarda "boş" olarak görünür)
  const sasRecheckLabel = (value) => {
    const v = toText(value);
    if (v === '1') return '1 - Evet';
    if (v === '0') return '0 - Hayır';
    return '';
  };

  const rows = receipt
    ? [
      {
        key: 'Status',
        label: 'Durum',
        oldValue: statusLabel(receipt.Status),
        newValue: statusLabel(form.Status),
        changed: form.Status !== receipt.Status
      },
      ...textFields.map((f) => ({
        ...f,
        oldValue: toText(receipt[f.key]),
        newValue: toText(form[f.key]),
        changed: toText(form[f.key]) !== toText(receipt[f.key])
      })),
      ...(isCustomer
        ? []
        : [{
          key: 'SasRecheck',
          label: "Mevcut SAS'ı Koru",
          oldValue: sasRecheckLabel(receipt.SasRecheck),
          newValue: sasRecheckLabel(form.SasRecheck),
          changed: toText(form.SasRecheck) !== toText(receipt.SasRecheck)
        }])
    ]
    : [];

  const sasRecheckRow = rows.find((r) => r.key === 'SasRecheck');
  // SAS No yoksa "mevcut SAS korunsun" seçeneği anlamsız; işaretlenemez
  const hasSasNo = toText(form.SasNo) !== '';
  const sasRecheckChecked = toText(form.SasRecheck) === '1';
  // İşaret kaldırıldığında: mevcut değer boşsa (NULL) boş kalsın, değilse 0 olsun
  const uncheckedSasRecheck = () => (toText(receipt?.SasRecheck) === '' ? null : '0');
  const changes = rows.filter((r) => r.changed);
  const statusChanged = changes.some((c) => c.key === 'Status');
  const canSave = changes.length > 0 && toText(form.Description) !== '';

  const resetField = (key) => {
    const original = key === 'Status'
      ? receipt.Status
      : key === 'SasRecheck'
        ? toText(receipt.SasRecheck) || null
        : toText(receipt[key]);
    setForm({ ...form, [key]: original });
  };

  const confirmSave = () => {
    setConfirmOpen(false);
    setLoading(true);
    dispatch(receiptManualUpdate({
      ReceiptNumber: receipt.ReceiptNumber,
      Status: form.Status,
      GpsNo: form.GpsNo,
      UdInvoiceOrderNo: form.UdInvoiceOrderNo,
      SasNo: form.SasNo,
      SdNo: form.SdNo,
      // null gönderilirse prosedür mevcut değeri korur
      SasRecheck: isCustomer ? null : form.SasRecheck,
      Description: form.Description,
      SicilNo: SessionUser?.UserId
    })).then((response) => {
      setLoading(false);
      const result = response.payload;
      if (result?.ErrorCode === '0') {
        showAlert(result.ErrorDescription, true);
        loadReceipt(receipt.ReceiptNumber);
      } else {
        showAlert(result?.ErrorDescription || 'Güncelleme sırasında hata oluştu.', false);
      }
    });
  };

  // Aynı kaydetmede değişen alanlar tek kartta gösterilir
  const historyGroups = history.reduce((groups, item) => {
    const group = groups.find((g) => g.OperationId === item.OperationId);
    if (group) {
      group.items.push(item);
    } else {
      groups.push({ ...item, items: [item] });
    }
    return groups;
  }, []);

  const metaItems = receipt
    ? [
      { label: 'Birim Kodu', value: receipt.UnitCode },
      { label: 'Başvuru No', value: receipt.ApplicationNumber },
      { label: 'Model Kodu', value: receipt.UserModelCode },
      { label: 'Model', value: receipt.ModelDesc },
      { label: 'Değişim Nedeni', value: receipt.ExchangeReasonDesc },
      { label: 'Durum Tarihi', value: receipt.StatusDate ? moment(receipt.StatusDate).format('DD.MM.YYYY HH:mm') : '' }
    ]
    : [];

  const renderFieldCard = (r) => (
    <Box className={`${classes.fieldCard} ${r.changed ? classes.fieldCardChanged : ''}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 30, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#263238' }}>{r.label}</Typography>
          {r.changed && (
            <Chip label="Değişti" size="small" sx={{ height: 20, fontSize: 11, borderRadius: '6px', backgroundColor: ORANGE, color: '#fff', fontWeight: 600 }} />
          )}
        </Box>
        {r.changed && (
          <Tooltip title="Mevcut değere dön">
            <IconButton size="small" onClick={() => resetField(r.key)}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography className={classes.caption} sx={{ mb: 0.5 }}>Mevcut</Typography>
          <Box sx={{ display: 'flex' }}>
            <Box className={classes.currentValue}>{r.oldValue || '-'}</Box>
          </Box>
        </Box>
        <Box className={`${classes.arrowCircle} ${r.changed ? classes.arrowCircleChanged : ''}`} sx={{ mb: { sm: '5px' }, alignSelf: { xs: 'center', sm: 'auto' } }}>
          <ArrowForwardIcon sx={{ fontSize: 18, transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
        </Box>
        <Box sx={{ flex: 1.2, minWidth: 0 }}>
          <Typography className={classes.caption} sx={{ mb: 0.5, color: r.changed ? '#e65100' : undefined }}>Yeni</Typography>
          {r.key === 'Status' ? (
            <Autocomplete
              size="small"
              options={statuses}
              value={statuses.find((s) => s.Id === form.Status) || null}
              getOptionLabel={(o) => `${o.Id} - ${o.Dsc}`}
              isOptionEqualToValue={(o, v) => o.Id === v.Id}
              onChange={(e, value) => setForm({ ...form, Status: value ? value.Id : receipt.Status })}
              noOptionsText="Durum bulunamadı"
              renderInput={(params) => <TextField {...params} placeholder="Durum seçiniz" sx={inputSx} />}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              value={form[r.key]}
              placeholder={`${r.label} giriniz`}
              inputProps={{ maxLength: r.maxLength }}
              onChange={(e) => {
                const next = { ...form, [r.key]: e.target.value };
                // SAS No silinirse "SAS korunsun" işareti anlamını yitirir
                if (r.key === 'SasNo' && toText(e.target.value) === '' && toText(form.SasRecheck) === '1') {
                  next.SasRecheck = uncheckedSasRecheck();
                }
                setForm(next);
              }}
              sx={inputSx}
            />
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AlertAndLoading
        Message={alertMessage}
        AlertType={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        loading={loading}
      />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Arama */}
        <Paper elevation={0} className={classes.card}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography className={classes.sectionTitle}>
                <ReceiptLongIcon sx={{ color: ORANGE }} /> Fiş Manuel Güncelleme
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Durumu veya numaraları güncellenmemiş fişleri buradan düzeltebilirsiniz.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end' } }}>
                <TextField
                  size="small"
                  placeholder="Fiş No giriniz"
                  value={receiptNumber}
                  sx={{ ...inputSx, flex: 1, maxWidth: 320 }}
                  onChange={(e) => setReceiptNumber(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && searchClick()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: ORANGE }} />
                      </InputAdornment>
                    ),
                    endAdornment: receipt && (
                      <InputAdornment position="end">
                        <Tooltip title="Temizle">
                          <IconButton size="small" onClick={clearClick}><CloseIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />
                <Button sx={darkButtonSx} startIcon={<SearchIcon />} onClick={searchClick}>
                  Getir
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {!receipt && (
          <Paper elevation={0} className={classes.card}>
            <Box className={classes.emptyState}>
              <ReceiptLongIcon sx={{ fontSize: 56, color: '#cfd8dc' }} />
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 600, color: '#78909c' }}>
                Güncellemek istediğiniz fişin numarasını girin
              </Typography>
              <Typography variant="body2">
                Fişin mevcut bilgileri ve güncellenebilecek alanlar burada listelenecek.
              </Typography>
            </Box>
          </Paper>
        )}

        {receipt && (
          <>
            {/* Fiş özeti */}
            <Paper elevation={0} className={classes.summary}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography className={classes.summaryCaption}>Fiş No</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{receipt.ReceiptNumber}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${receipt.Status} - ${receipt.StatusDesc || ''}`} sx={{ borderRadius: '8px', backgroundColor: ORANGE, color: '#fff', fontWeight: 600 }} />
                    <Chip label={receipt.CustomerTypeDesc} variant="outlined" sx={{ borderRadius: '8px', borderColor: '#cfd8dc', color: DARK, fontWeight: 600 }} />
                    {receipt.SubStatusDesc && (
                      <Chip label={`Ara Durum: ${receipt.SubStatusDesc}`} variant="outlined" sx={{ borderRadius: '8px', borderColor: '#cfd8dc', color: DARK }} />
                    )}
                  </Box>
                </Box>
                <Tooltip title="Manuel güncelleme geçmişi">
                  <IconButton
                    onClick={() => setHistoryOpen(true)}
                    sx={{ color: ORANGE, backgroundColor: DARK, '&:hover': { backgroundColor: '#212121' } }}
                  >
                    <Badge badgeContent={historyGroups.length} color="warning">
                      <HistoryIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 5, rowGap: 1.5 }}>
                {metaItems.map((m) => (
                  <Box key={m.label}>
                    <Typography className={classes.summaryCaption}>{m.label}</Typography>
                    <Typography className={classes.summaryValue}>{toText(m.value) || '-'}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Bilgileri güncelle */}
            <Paper elevation={0} className={classes.card}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography className={classes.sectionTitle}>
                  <EditIcon sx={{ color: ORANGE }} /> Bilgileri Güncelle
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#78909c', backgroundColor: '#f5f7f8', borderRadius: '10px', px: 1.5, py: 0.75 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    {isCustomer
                      ? 'Müşteri tipi "Müşteri" olduğu için Gps No ve Ud Fatura No güncellenebilir.'
                      : `Müşteri tipi "${receipt.CustomerTypeDesc}" olduğu için Sas No, Sd No ve Mevcut SAS'ı Koru güncellenebilir.`}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>{renderFieldCard(rows[0])}</Grid>
                {rows.slice(1).filter((r) => r.key !== 'SasRecheck').map((r) => (
                  <Grid item xs={12} lg={6} key={r.key}>{renderFieldCard(r)}</Grid>
                ))}
                {sasRecheckRow && (
                  <Grid item xs={12}>
                    <Box
                      className={`${classes.fieldCard} ${sasRecheckRow.changed ? classes.fieldCardChanged : ''}`}
                      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, py: '6px !important' }}
                    >
                      <Tooltip title={hasSasNo ? '' : 'SAS No olmadan işaretlenemez'}>
                        <span>
                          <Checkbox
                            size="small"
                            checked={sasRecheckChecked}
                            disabled={!hasSasNo && !sasRecheckChecked}
                            onChange={(e) => {
                              setForm({ ...form, SasRecheck: e.target.checked ? '1' : uncheckedSasRecheck() });
                            }}
                            sx={{ p: 0.5, color: '#90a4ae', '&.Mui-checked': { color: ORANGE } }}
                          />
                        </span>
                      </Tooltip>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#263238' }}>Mevcut SAS'ı Koru</Typography>
                          <Typography sx={{ fontSize: 12, color: '#78909c' }}>
                            Mevcut: <b>{sasRecheckRow.oldValue || 'Boş'}</b>
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: !hasSasNo ? '#c62828' : sasRecheckChecked ? '#2e7d32' : '#546e7a' }}>
                          {!hasSasNo
                            ? 'SAS No olmadığı için işaretlenemez; yeni SAS oluşturulacaktır.'
                            : sasRecheckChecked
                              ? 'İşaretli: Var olan SAS korunacak, yeni SAS oluşturulmayacaktır.'
                              : 'İşaretsiz: Yeni SAS oluşturulacaktır.'}
                        </Typography>
                      </Box>
                      {sasRecheckRow.changed && (
                        <>
                          <Chip label="Değişti" size="small" sx={{ height: 20, fontSize: 11, borderRadius: '6px', backgroundColor: ORANGE, color: '#fff', fontWeight: 600 }} />
                          <Tooltip title="Mevcut değere dön">
                            <IconButton size="small" onClick={() => resetField('SasRecheck')} sx={{ ml: 'auto' }}>
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Açıklama"
                    placeholder="Bu güncellemeyi neden yaptığınızı kısaca yazınız"
                    required
                    value={form.Description}
                    inputProps={{ maxLength: 500 }}
                    helperText={`${toText(form.Description).length}/500`}
                    FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                    onChange={(e) => setForm({ ...form, Description: e.target.value })}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ color: changes.length ? '#e65100' : '#90a4ae', fontWeight: 600 }}>
                  {changes.length
                    ? `${changes.length} alan güncellenecek${toText(form.Description) ? '' : ' — kaydetmek için açıklama giriniz'}`
                    : 'Henüz bir değişiklik yapılmadı'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button sx={lightButtonSx} startIcon={<RestartAltIcon />} disabled={!changes.length} onClick={() => fillForm(receipt)}>
                    Tümünü Geri Al
                  </Button>
                  <Button sx={darkButtonSx} startIcon={<SaveIcon />} disabled={!canSave} onClick={() => setConfirmOpen(true)}>
                    Kaydet
                  </Button>
                </Box>
              </Box>
            </Paper>
          </>
        )}
      </CardContent>

      {/* Kaydet önizleme / onay */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: RADIUS } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <WarningAmberIcon sx={{ color: ORANGE }} /> Emin misiniz?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <b>{receipt?.ReceiptNumber}</b> numaralı fişte aşağıdaki değişiklikler yapılacaktır:
          </Typography>
          {changes.map((c) => (
            <Box key={c.key} className={classes.previewRow}>
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                <b>{c.label}</b> alanı <b>{c.oldValue || '(boş)'}</b> değerinden <b style={{ color: '#e65100' }}>{c.newValue || '(boş)'}</b> değerine geçecektir.
              </Typography>
              <ChangeChips oldValue={c.oldValue} newValue={c.newValue} />
            </Box>
          ))}
          {statusChanged && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1, p: 1.5, borderRadius: '12px', backgroundColor: '#fbe9e7', color: '#bf360c' }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, mt: '2px' }} />
              <Typography variant="body2">
                Durum değişikliği fişin iş akışını etkiler; ilgili entegrasyonlar ve atamalar yeni duruma göre güncellenir.
              </Typography>
            </Box>
          )}
          {statusChanged && form.Status === 17 && (
            <Box className={classes.previewRow} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <b>Tamamlandı</b> durumuna geçişte başvurunun durumu da <b style={{ color: '#e65100' }}>14</b> olarak güncellenecek ve işlem tarihi <b>şu an</b> olarak ayarlanacaktır.
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: '12px' }}>
            <Typography className={classes.caption}>Açıklama</Typography>
            <Typography variant="body2">{form.Description}</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button sx={lightButtonSx} onClick={() => setConfirmOpen(false)}>Vazgeç</Button>
          <Button sx={darkButtonSx} startIcon={<SaveIcon />} onClick={confirmSave}>Evet, Güncelle</Button>
        </DialogActions>
      </Dialog>

      {/* Geçmiş */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: RADIUS } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography component="span" className={classes.sectionTitle}>
            <HistoryIcon sx={{ color: ORANGE }} /> Manuel Güncelleme Geçmişi — {receipt?.ReceiptNumber}
          </Typography>
          <IconButton onClick={() => setHistoryOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {historyGroups.length === 0 ? (
            <Box className={classes.emptyState} sx={{ py: '32px !important' }}>
              <HistoryIcon sx={{ fontSize: 48, color: '#cfd8dc' }} />
              <Typography variant="body2">Bu fiş için daha önce manuel güncelleme yapılmamış.</Typography>
            </Box>
          ) : (
            historyGroups.map((g, index) => (
              <Box key={g.OperationId}>
                {index > 0 && <Divider />}
                <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                  <Avatar sx={{ width: 38, height: 38, fontSize: 14, bgcolor: DARK, color: ORANGE }}>
                    {initials(g.UserName)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{g.UserName || 'Bilinmeyen kullanıcı'}</Typography>
                      <Typography variant="caption" color="text.secondary">{moment(g.Date).format('DD.MM.YYYY HH:mm')}</Typography>
                    </Box>
                    {g.Description && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                        “{g.Description}”
                      </Typography>
                    )}
                    {g.items.map((i) => (
                      <Box key={i.FieldName} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>{displayFieldName(i.FieldName)}</Typography>
                        <ChangeChips oldValue={i.OldValue} newValue={i.NewValue} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReceiptManualUpdateDynamic;
